import { useCallback, useMemo } from 'react'
import { useIntl } from 'react-intl'
import { rem } from 'polished'
import styled from 'styled-components'
import Group from 'components/utils/Group'
import TextButton from 'LuxKit/Button/TextButton'
import TextLink from 'LuxKit/TextLink'
import BodyText from 'LuxKit/Typography/BodyText'
import Heading from 'LuxKit/Typography/Heading'
import TextLoadingBox from 'LuxKit/Typography/TextLoadingBox'
import MessageBanner from 'LuxKit/Banners/MessageBanner'
import PriceRowPrice from 'LuxKit/PricePoints/PriceRowPrice'
import CartReviewPriceStack from 'components/Pages/CartReviewPage/CartReviewPriceStack'
import LineInfoCircleIcon from 'LuxKit/Icons/line/LineInfoCircleIcon'
import LineTrashAltIcon from 'LuxKit/Icons/line/LineTrashAltIcon'
import LinePenIcon from 'LuxKit/Icons/line/LinePenIcon'
import LineTicketIcon from 'LuxKit/Icons/line/LineTicketIcon'
import LoyaltyPointsEarnWithBreakdownModal from 'loyalty/components/Earn/LoyaltyPointsEarnWithBreakdownModal'
import { DMY_CASUAL_FORMAT, INTL_DMY_CASUAL_FORMAT } from 'constants/dateFormats'
import { useAppSelector } from 'hooks/reduxHooks'
import { intlDateFormatter } from 'lib/datetime/dateUtils'
import { getFormattedDateByTimezone } from 'lib/experiences/experienceUtils'
import { generateLoyaltyPointsCalculatorExperienceOptions } from 'loyalty/lib/pointsCalculation/calculatorOptionsGenerators'
import CartPanelAccommodationTileLayout from './CartPanelAccommodationTileLayout'
import type { CartPanelExperienceItem } from '../hooks/useCartPanelItems'
import {
  isCartPanelExperienceUnavailableStatus,
  isExperienceBuyNowBookLaterBookingType,
} from '../utils/cartPanelUtils'
import { useTripPlannerEventBus } from 'tripPlanner/assistant/contexts/TripPlannerEventBus'

const TicketCard = styled.div`
  border: 1px solid ${props => props.theme.palette.neutral.default.five};
  border-radius: ${props => props.theme.borderRadius.M};
  padding: ${rem(12)} ${rem(16)};
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: ${rem(12)};
  width: 100%;
  max-width: ${rem(500)};
`

const TicketCardContent = styled.div`
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: ${rem(4)};
`

const CheckoutDetailsBanner = styled(MessageBanner)`
  > div {
    padding-top: ${rem(8)};
    padding-bottom: ${rem(8)};
  }

  a,
  button {
    --button-background: var(--palette-neutral-default-one, #363A45);
    --button-internal-default-color: var(--palette-neutral-default-one, #363A45);
    --button-internal-default-border-color: var(--palette-neutral-default-one, #363A45);
    --button-internal-hover-color: var(--palette-neutral-surface-dark-contrast, #FFFFFF);
    --button-internal-hover-background-color: var(--palette-neutral-default-one, #363A45);
    --button-internal-hover-border-color: var(--palette-neutral-default-one, #363A45);
    --button-internal-active-color: var(--palette-neutral-surface-dark-contrast, #FFFFFF);
    --button-internal-active-background-color: var(--palette-neutral-default-one, #363A45);
    --button-internal-active-border-color: var(--palette-neutral-default-one, #363A45);
    --button-internal-focus-color: var(--palette-neutral-default-one, #363A45);
    --button-internal-focus-border-color: var(--palette-neutral-default-one, #363A45);
  }
`

const PriceLoadingState = styled.div`
  display: flex;
  justify-content: flex-end;
  min-width: ${rem(96)};
`

interface Props {
  item: CartPanelExperienceItem
  onToggleInclude: (item: CartPanelExperienceItem) => void
  onChooseTickets: (item: CartPanelExperienceItem) => void
  onChooseDate: (item: CartPanelExperienceItem) => void
  onChooseTime: (item: CartPanelExperienceItem) => void
  onChooseDifferentTickets: (item: CartPanelExperienceItem) => void
  onDelete: (item: CartPanelExperienceItem) => void
  isStreaming: boolean
}

function CartPanelExperienceTile({
  item,
  onToggleInclude,
  onDelete,
  isStreaming,
}: Props) {
  const intl = useIntl()
  const experience = useAppSelector(state => state.experience.experiences[item.experienceId])
  const eventBus = useTripPlannerEventBus()
  const pointsEarnCalculationRequest = useMemo(() => generateLoyaltyPointsCalculatorExperienceOptions(experience, {
    offerId: item.experienceId,
    price: item.pricing.price,
    memberPrice: item.pricing.memberPrice ?? 0,
    skip: !item.pricing.price,
  }), [experience, item.experienceId, item.pricing.memberPrice, item.pricing.price])

  const handleDelete = useCallback(() => onDelete(item), [onDelete, item])

  const handleCardClick = useCallback(() => {
    eventBus.dispatch({ type: 'open_offer_drawer', offerId: item.experienceId, offerType: 'experience', scrollToPricing: true })
  }, [item.experienceId, eventBus])

  const handleEditAction = useCallback(() => {
    eventBus.dispatch({ type: 'open_offer_drawer', offerId: item.experienceId, offerType: 'experience', scrollToPricing: true })
  }, [item.experienceId, eventBus])

  const bookingDateLabel = useMemo(() => {
    if (!item.date) return undefined
    const formattedDate = intlDateFormatter(item.date, INTL_DMY_CASUAL_FORMAT, intl.locale)
    if (!item.time) return formattedDate
    return intl.formatMessage(
      {
        id: 'EsAeJ_-OedevVs7U56KT',
        defaultMessage: '{date} · {time}',
        description: 'Experience cart panel date and selected time label',
      },
      { date: formattedDate, time: item.time },
    )
  }, [intl, item.date, item.time])

  const itineraryDateLabel = useMemo(() => {
    if (item.date || !item.itineraryDate) return undefined
    return intl.formatMessage(
      {
        id: 'yTFyOdWc4SF2VBbaje7r',
        defaultMessage: 'Planned for {date}',
        description: 'Experience cart panel label showing the itinerary day when the ticket itself is open dated',
      },
      { date: intlDateFormatter(item.itineraryDate, INTL_DMY_CASUAL_FORMAT, intl.locale) },
    )
  }, [intl, item.date, item.itineraryDate])

  const selectedTickets = useMemo(
    () => item.tickets.filter(ticket => ticket.count > 0),
    [item.tickets],
  )

  const hasSelectedTicketDates = useMemo(
    () => selectedTickets.some(ticket => !!ticket.date),
    [selectedTickets],
  )

  const openDatedBookingCopy = useMemo(() => {
    const bookingType = experience?.bookingType ?? item.bookingType
    if (selectedTickets.length === 0 || hasSelectedTicketDates || item.date || !isExperienceBuyNowBookLaterBookingType(bookingType)) return undefined

    const timezone = experience?.location.timezone
    const bookByDate = experience?.bookByDate ?? experience?.expirationDate
    const formattedBookByDate = timezone ? getFormattedDateByTimezone(bookByDate, timezone, DMY_CASUAL_FORMAT) : undefined
    const formattedExpirationDate = timezone ? getFormattedDateByTimezone(experience?.expirationDate, timezone, DMY_CASUAL_FORMAT) : undefined

    if (formattedBookByDate && formattedExpirationDate && formattedExpirationDate !== formattedBookByDate) {
      return intl.formatMessage(
        {
          id: 'LF2gkZA1aC4PJ6DxDo5o',
          defaultMessage: 'This ticket checks out as an open-dated voucher. Your itinerary date is planned, but the provider date is not reserved at checkout. Book by {bookByDate} for a date up to {expirationDate}.',
          description: 'Experience cart panel label for an open dated ticket with booking deadline and last travel date',
        },
        { bookByDate: formattedBookByDate, expirationDate: formattedExpirationDate },
      )
    }

    if (formattedBookByDate) {
      return intl.formatMessage(
        {
          id: 'RB0M1bP8__3RuPCNPdvb',
          defaultMessage: 'This ticket checks out as an open-dated voucher. Your itinerary date is planned, but the provider date is not reserved at checkout. Book by {bookByDate}.',
          description: 'Experience cart panel label for an open dated ticket with booking deadline',
        },
        { bookByDate: formattedBookByDate },
      )
    }

    return intl.formatMessage({
      id: 'wVTAVdBaWFORt29iTDhO',
      defaultMessage: 'This ticket checks out as an open-dated voucher. Your itinerary date is planned, but the provider date is not reserved at checkout.',
      description: 'Experience cart panel label for an experience ticket that does not require choosing a date at purchase time',
    })
  }, [experience, hasSelectedTicketDates, intl, item.bookingType, item.date, selectedTickets.length])

  const statusContent = useMemo(() => {
    switch (item.cartStatus) {
      case 'needs_tickets':
        return {
          label: intl.formatMessage({
            id: 'ODAAg_MmBm0W2QfmWsfR',
            defaultMessage: 'Tickets needed',
            description: 'Attention indicator prompting the user to choose tickets for an experience in the cart panel',
          }),
          action: intl.formatMessage({
            id: 'PJVaoiDLW8-FbfxBYn0N',
            defaultMessage: 'Choose tickets',
            description: 'Action button to choose tickets for an experience in the cart panel',
          }),
          onAction: handleEditAction,
        }
      case 'needs_dates':
        return {
          label: intl.formatMessage({
            id: 'Riw2p5lzxaSylvg3WRzU',
            defaultMessage: 'Date needed',
            description: 'Attention indicator prompting the user to choose a date for an experience in the cart panel',
          }),
          action: intl.formatMessage({
            id: 'AwXhLqICm1mppU92SRe3',
            defaultMessage: 'Choose date',
            description: 'Action button to choose a date for an experience in the cart panel',
          }),
          onAction: handleEditAction,
        }
      case 'needs_time':
        return {
          label: intl.formatMessage({
            id: 'LJHwW2XCTx4qQ8aCJsmA',
            defaultMessage: 'Time needed',
            description: 'Attention indicator prompting the user to choose a time for an experience in the cart panel',
          }),
          action: intl.formatMessage({
            id: 'miNa47q6QRupOlrFwRsT',
            defaultMessage: 'Choose time',
            description: 'Action button to choose a time for an experience in the cart panel',
          }),
          onAction: handleEditAction,
        }
      case 'date_unavailable':
        return {
          label: intl.formatMessage({
            id: 'PLYbcNvVhWh33oFzAo0i',
            defaultMessage: 'Selected date unavailable',
            description: 'Attention indicator shown when the selected experience date is no longer available',
          }),
          action: intl.formatMessage({
            id: 'YVy__5mq75wAZCMBuhJh',
            defaultMessage: 'Choose another date',
            description: 'Action button to choose a replacement date for an experience in the cart panel',
          }),
          onAction: handleEditAction,
        }
      case 'time_unavailable':
        return {
          label: intl.formatMessage({
            id: 'cHfTEEQWrihGdz_nL-nJ',
            defaultMessage: 'Selected time unavailable',
            description: 'Attention indicator shown when the selected experience time is no longer available',
          }),
          action: intl.formatMessage({
            id: 'ptqOFLI9YXZ2-bzxERcx',
            defaultMessage: 'Choose another time',
            description: 'Action button to choose a replacement time for an experience in the cart panel',
          }),
          onAction: handleEditAction,
        }
      case 'tickets_unavailable':
        return {
          label: intl.formatMessage({
            id: '01EbrjRC8BdPw4AazCkJ',
            defaultMessage: 'Tickets unavailable',
            description: 'Attention indicator shown when selected experience tickets are unavailable',
          }),
          action: intl.formatMessage({
            id: '6VZ3DlrTqlirLD-k371g',
            defaultMessage: 'Choose different tickets',
            description: 'Action button to choose replacement tickets for an experience in the cart panel',
          }),
          onAction: handleEditAction,
        }
      default:
        return undefined
    }
  }, [handleEditAction, intl, item.cartStatus])

  const checkoutDetailsBannerDescription = useMemo(() => {
    switch (item.cartStatus) {
      case 'needs_tickets':
        return intl.formatMessage({
          id: '0XFs8Wh_XyMY8lRfh5BO',
          defaultMessage: 'Choose tickets to include in checkout',
          description: 'Warning banner prompting the user to choose experience tickets so the item can be included in checkout',
        })
      case 'needs_dates':
        return intl.formatMessage({
          id: 'Ds0Bj8Og_VCfQV63NMhd',
          defaultMessage: 'Choose a date to include in checkout',
          description: 'Warning banner prompting the user to choose an experience date so the item can be included in checkout',
        })
      case 'needs_time':
        return intl.formatMessage({
          id: '5pGbfiCRfC5EHPfczTn_',
          defaultMessage: 'Choose a time to include in checkout',
          description: 'Warning banner prompting the user to choose an experience time so the item can be included in checkout',
        })
      case 'date_unavailable':
        return intl.formatMessage({
          id: 'wUMzKO48nBcnos6dfeKr',
          defaultMessage: 'Choose another date to include in checkout',
          description: 'Warning banner prompting the user to replace an unavailable experience date before checkout',
        })
      case 'time_unavailable':
        return intl.formatMessage({
          id: '2jnBsTVwAj5-JqWC71nk',
          defaultMessage: 'Choose another time to include in checkout',
          description: 'Warning banner prompting the user to replace an unavailable experience time before checkout',
        })
      case 'tickets_unavailable':
        return intl.formatMessage({
          id: 'rDA701rMnvQSP2fxs5J3',
          defaultMessage: 'Choose different tickets to include in checkout',
          description: 'Warning banner prompting the user to replace unavailable experience tickets before checkout',
        })
      default:
        return undefined
    }
  }, [intl, item.cartStatus])

  const checkoutDetailsBanner = checkoutDetailsBannerDescription && statusContent ? (
    <CheckoutDetailsBanner
      kind="warning"
      density="compact"
      primaryActionButtonProps={{
        children: statusContent.action,
        onClick: statusContent.onAction,
        disabled: isStreaming,
        size: 'small',
      }}
    >
      {checkoutDetailsBannerDescription}
    </CheckoutDetailsBanner>
  ) : undefined

  const isExperienceUnavailable = isCartPanelExperienceUnavailableStatus(item.cartStatus)

  const header = (
    <Group direction="vertical" gap={4}>
      <Heading variant="heading4">{item.name}</Heading>
      {bookingDateLabel && <BodyText variant="medium" colour="neutral-three">{bookingDateLabel}</BodyText>}
      {itineraryDateLabel && <BodyText variant="medium" colour="neutral-three">{itineraryDateLabel}</BodyText>}
      {item.locationName && <BodyText variant="medium" colour="neutral-three">{item.locationName}</BodyText>}
    </Group>
  )

  const changeTicketsLabel = intl.formatMessage({
    id: 'cYfEugz5ftPhNMAvCuVB',
    defaultMessage: 'Change tickets',
    description: 'Action link to modify the selected tickets for an experience in the cart panel',
  })

  const ticketCard = selectedTickets.length > 0 ? (
    <TicketCard>
      <TicketCardContent>
        {selectedTickets.map(ticket => (
          <BodyText key={ticket.ticketId} variant="medium">
            {intl.formatMessage(
              {
                id: 'qHEhpqUYI06jFsQ33xqb',
                defaultMessage: '{count} × {name}',
                description: 'Experience ticket count and name in the cart panel',
              },
              { count: ticket.count, name: ticket.name },
            )}
          </BodyText>
        ))}
      </TicketCardContent>
      {!statusContent && (
        <TextLink size="caption" startIcon={<LinePenIcon />} onClick={handleEditAction} disabled={isStreaming}>
          {changeTicketsLabel}
        </TextLink>
      )}
    </TicketCard>
  ) : undefined

  const attention = (
    <Group direction="vertical" gap={8}>
      {openDatedBookingCopy && (
        <MessageBanner
          kind="informational-primary"
          density="compact"
          icon={<LineInfoCircleIcon />}
          heading={intl.formatMessage({
            id: 'RKajGQl_r5cRYCQIq1wh',
            defaultMessage: 'Open-dated ticket',
            description: 'Heading for an experience cart panel banner explaining the user will select dates after checkout',
          })}
        >
          {openDatedBookingCopy}
        </MessageBanner>
      )}
      {item.pricing.price && !isExperienceUnavailable && (
        <LoyaltyPointsEarnWithBreakdownModal
          calculationRequests={[pointsEarnCalculationRequest]}
          calculationType="exact"
          offer={experience}
        />
      )}
    </Group>
  )

  const deleteAction = (
    <TextButton kind="secondary" size="small" shape="round" startIcon={<LineTrashAltIcon />} onClick={handleDelete} disabled={isStreaming}>
      {intl.formatMessage({
        id: 'jDEZjhV3eo-cnxIBHOtR',
        defaultMessage: 'Delete',
        description: 'Button to permanently delete an item from the trip planner cart panel',
      })}
    </TextButton>
  )

  const isExactTicketPrice = item.cartStatus === 'active' && item.tickets.length > 0
  const price = !isExperienceUnavailable && item.pricing.loading ? (
    <PriceLoadingState>
      <TextLoadingBox typography="heading5" width="8ch" />
    </PriceLoadingState>
  ) : item.pricing.price && !isExperienceUnavailable ? (
    isExactTicketPrice ? (
      <CartReviewPriceStack
        price={item.pricing.price}
        memberPrice={item.pricing.memberPrice}
        value={item.pricing.value ?? 0}
      />
    ) : (
      <PriceRowPrice
        price={item.pricing.price}
        priceFormat="preciseDollar"
        saleUnit=""
        size="S"
      />
    )
  ) : undefined

  return (
    <CartPanelAccommodationTileLayout
      item={item}
      onToggleInclude={onToggleInclude}
      onCardClick={handleCardClick}
      fallbackIcon={<LineTicketIcon size="XL" />}
      topBanner={checkoutDetailsBanner}
      header={header}
      roomCard={ticketCard}
      attention={attention}
      deleteAction={deleteAction}
      price={price}
    />
  )
}

export default CartPanelExperienceTile
